import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import { geographiesService } from "../../services/reference/geographies.service";
import {
    addEntityActionLabel,
    appRoutes,
    buildRecordLifecycleConfirmProps,
    EntityLabels,
    formatReferenceSourceDisplay,
    ListPageTitles,
    recordLifecycleListConfirmBodies,
    referenceAdminEditPath,
    referenceAdminNewPath,
    showToastSuccess,
    resolveActionErrorMessage,
} from "../../utils";
import {
    buildGeoAdminPagedListActions,
    getGeoAdminActionSuccessMessage,
    type GeoAdminConfirmKind,
} from "./geoAdminListActions";

type ConfirmKind = GeoAdminConfirmKind | null;

const GeographiesListPage = () => {
    const navigate = useNavigate();

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmGeoId, setConfirmGeoId] = useState<string | null>(null);
    const [confirmGeoLabel, setConfirmGeoLabel] = useState("");

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmGeoId(null);
        setConfirmGeoLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmGeoId || !confirmKind) return;
        const id = confirmGeoId;
        const op =
            confirmKind === "disable"
                ? geographiesService.disable(id)
                : confirmKind === "enable"
                  ? geographiesService.enable(id)
                  : confirmKind === "softDelete"
                    ? geographiesService.softDelete(id)
                    : geographiesService.restore(id);
        op.then(() => {
            showToastSuccess(getGeoAdminActionSuccessMessage(confirmKind, confirmGeoLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(resolveActionErrorMessage(e));
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: { id: string; code: string }) => {
        setConfirmKind(kind);
        setConfirmGeoId(item.id);
        setConfirmGeoLabel(item.code);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.geography,
                confirmGeoLabel,
                recordLifecycleListConfirmBodies(confirmGeoLabel)
            ),
        [confirmKind, confirmGeoLabel]
    );

    const columns: IColumn[] = useMemo(
        () => [
            {
                member: "code",
                renderAs: ColumnRender.customRenderer,
                title: "Code",
                width: 140,
                customRenderer: (item: { code?: string }) => (
                    <td className="text-start">
                        <span className="font-monospace">{item.code ?? ""}</span>
                    </td>
                ),
            } as IColumn,
            { member: "geographyType", renderAs: ColumnRender.text, title: "Type", width: 100 } as IColumn,
            { member: "description", renderAs: ColumnRender.text, title: "Description", width: 300 } as IColumn,
            {
                member: "source",
                renderAs: ColumnRender.customRenderer,
                title: "Source",
                width: 120,
                customRenderer: (item: { source?: string }) => (
                    <td className="text-start">{formatReferenceSourceDisplay(item.source)}</td>
                ),
            } as IColumn,
            { member: "isActive", renderAs: ColumnRender.checkCross, title: "Active", width: 90, align: "center" } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(
        () => [
            { title: "Code", member: "code", type: FilterType.text } as IFilter,
            {
                title: "Type",
                member: "geographyType",
                type: FilterType.select,
                options: {
                    All: null,
                    REGION: "Region",
                    "SUB-REGION": "Sub-Region",
                    DISTRICT: "District",
                },
            } as IFilter,
            { title: "Description", member: "description", type: FilterType.text } as IFilter,
            { title: "Source", member: "source", type: FilterType.text } as IFilter,
            {
                title: "Active",
                member: "isActive",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
        ],
        []
    );

    const itemActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: "Edit",
                onClick: (item: { id: string }) => navigate(referenceAdminEditPath("geography", item.id)),
                primary: true,
            } as IPagedListAction,
            ...buildGeoAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ],
        [navigate]
    );

    const headActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: addEntityActionLabel(EntityLabels.geography),
                icon: faPlus,
                onClick: () => navigate(referenceAdminNewPath("geography")),
                primary: true,
            } as IPagedListAction,
        ],
        [navigate]
    );

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.geography}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: EntityLabels.referenceData, to: appRoutes.codes },
                            { text: ListPageTitles.geography },
                        ]}
                    />
                    <h1>{ListPageTitles.geography}</h1>
                    <p className="text-muted">
                        Region and district codes for regulatory reporting. Codes must be unique across all area types.
                        Type is fixed after creation.
                    </p>
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    <PagedList
                        columns={columns}
                        filters={filters}
                        headActions={headActions}
                        itemActions={itemActions}
                        rowActionsAriaLabel="Geography actions"
                        showDeletedRowIndicator
                        defaultSortBy="Code"
                        defaultSortOrder={SortOrder.ascending}
                        getUrl="/api/referencedata/geographies/all"
                        rowFormatter={() => null}
                        persistanceKey="GeographiesListPage"
                        refreshNumber={refreshNumber}
                    />
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { GeographiesListPage };
